import { Input as AntdInput } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import classNames from 'classnames';
import React, { ChangeEvent, FC, memo, ReactNode } from 'react';
import { Popup } from 'shared/ui/Popup';
import { ReactComponent as InfoIcon } from 'shared/assets/icons/InfoIcon.svg';

import s from './index.module.scss';

type InputSize = 'extraSmall' | SizeType;

interface ModernInputProps {
  value?: Nullable<string>;
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  prefix?: ReactNode;
  className?: string;
  bordered?: boolean;
  size?: InputSize;
  disabled?: boolean;
  maxLength?: number;
  requiredMark?: boolean;
  tooltip?: string;
  label?: string;
  containerClassName?: string;
  onTouchStart?: (value: CustomAny) => void;
  readOnly?: boolean;
  uppercase?: boolean;
  labelClassName?: string;
}

export const ModernInput: FC<ModernInputProps> = memo((props) => {
  const {
    value,
    placeholder,
    prefix,
    className,
    onChange,
    disabled,
    maxLength,
    bordered = false,
    size = 'middle',
    requiredMark,
    tooltip,
    label,
    containerClassName,
    onTouchStart,
    readOnly,
    uppercase,
    labelClassName,
  } = props;

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const inputValue = uppercase ? e.target.value.toUpperCase() : e.target.value;

    onChange?.(inputValue, e);
  };

  const handleTouch = (): void => {
    onTouchStart?.(value || []);
  };

  return (
    <div className={classNames('flex flex-col', containerClassName)}>
      <div className="flex">
        {label && <div className={classNames('mb-2.5 mr-2', labelClassName)}>{label}</div>}
        {tooltip && (
          <Popup title={label} description={tooltip}>
            <InfoIcon width={22} height={22} className="[&>path]:stroke-primaryLight" />
          </Popup>
        )}
      </div>
      {requiredMark && <div className="text-error text-2xl absolute z-10 top-0 left-1 pointer-events-none">*</div>}
      <AntdInput
        className={classNames(s.input, s[size], className)}
        variant={bordered ? 'outlined' : 'borderless'}
        placeholder={placeholder}
        prefix={prefix}
        value={value || undefined}
        onChange={handleChange}
        disabled={disabled}
        maxLength={maxLength}
        onClick={handleTouch}
        readOnly={readOnly}
      />
    </div>
  );
});
